import React,{useEffect,useState} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import {FiChevronDown,FiFileText,FiClock,FiAlertTriangle} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {applyPageSEO} from '../utils/seo';

const policies=[
  {
    id: 'misconduct',
    title: 'Reporting Driving Instructor Misconduct',
    url: 'https://media-manager-c.questera.ai/greta-media/0b3af998e770acea81dd65e1f88cbe23e5433846e0d8905ff7ba265c9308956ef02252169608e8dd98a402f9c5748de4/documents/YXBwbGljYXRpb24vcGRm/3956260e43ce0b2a4d1fccdc1eacad97.pdf'
  },
  {
    id: 'cancellation',
    title: 'Cancellation Policy',
    url: 'https://media-manager-c.questera.ai/greta-media/0b3af998e770acea81dd65e1f88cbe23e5433846e0d8905ff7ba265c9308956ef02252169608e8dd98a402f9c5748de4/documents/YXBwbGljYXRpb24vcGRm/7996d71fabe78626b849f073cb258303.pdf'
  },
  {
    id: 'privacy',
    title: 'Privacy Policy',
    url: 'https://media-manager-c.questera.ai/greta-media/0b3af998e770acea81dd65e1f88cbe23e5433846e0d8905ff7ba265c9308956ef02252169608e8dd98a402f9c5748de4/documents/YXBwbGljYXRpb24vcGRm/a7a4b885f0faaaff85d706dfed231f72.pdf'
  },
  {
    id: 'terms',
    title: 'Terms and Conditions',
    url: 'https://media-manager-c.questera.ai/greta-media/0b3af998e770acea81dd65e1f88cbe23e5433846e0d8905ff7ba265c9308956ef02252169608e8dd98a402f9c5748de4/documents/YXBwbGljYXRpb24vcGRm/df8ca171e7d9225bf7639bfb5f6fd95d.pdf'
  }
];

const toGoogleViewerUrl=(pdfUrl)=> `https://docs.google.com/gview?url=${encodeURIComponent(pdfUrl)}&embedded=true`;

function PolicyCard({policy,isActive,onToggle}) {
  return (
    <motion.div
      initial={{opacity: 0,y: 20}}
      animate={{opacity: 1,y: 0}}
      className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-5 md:p-7 hover:bg-gray-50 transition-colors text-left"
        aria-expanded={isActive}
      >
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl transition-colors ${isActive ? 'bg-brand-gold text-brand-navy' : 'bg-brand-navy/5 text-brand-navy'}`}>
            <SafeIcon icon={FiFileText} className="text-xl md:text-2xl" />
          </div>
          <h2 className="font-display text-lg md:text-2xl text-brand-navy">{policy.title}</h2>
        </div>
        <motion.div
          animate={{rotate: isActive ? 180 : 0}}
          transition={{duration: 0.3}}
          className="shrink-0 ml-3"
        >
          <SafeIcon icon={FiChevronDown} className="text-2xl text-brand-gold" />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {isActive && (
          <motion.div
            key="body"
            initial={{height: 0,opacity: 0}}
            animate={{height: 'auto',opacity: 1}}
            exit={{height: 0,opacity: 0}}
            transition={{duration: 0.35,ease: 'easeInOut'}}
            className="border-t border-gray-100 overflow-hidden"
          >
            <div className="p-3 md:p-5 bg-brand-offwhite/60">
              <div className="w-full rounded-xl overflow-hidden border-2 border-gray-200 bg-white shadow-inner" style={{height: 'clamp(420px,72vh,820px)'}}>
                <iframe
                  src={toGoogleViewerUrl(policy.url)}
                  title={policy.title}
                  className="w-full h-full border-0"
                  allow="autoplay"
                  loading="lazy"
                />
              </div>
              <p className="text-center text-xs text-gray-400 mt-3">Document is displayed in read-only mode.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function CompliancePage() {
  const [activePolicy,setActivePolicy]=useState(null);

  useEffect(()=> {
    applyPageSEO({
      title: 'Cancellation Policy, Privacy Policy & Terms | Drive Pal Driving School Goulburn',
      description: 'Read Drive Pal Driving School compliance documents including driving instructor reporting information, cancellation policy, privacy policy, and terms and conditions.',
      keywords: 'Drive Pal cancellation policy, driving school cancellation policy Goulburn, driving school privacy policy NSW, driving lesson terms and conditions Goulburn, driving instructor misconduct reporting NSW, driving school compliance Goulburn NSW',
      url: 'https://drivepal.au/#/compliance',
      ogTitle: 'Policies & Compliance | Drive Pal Driving School Goulburn NSW',
      ogDescription: 'Driving instructor reporting information, cancellation policy, privacy policy, and terms and conditions for Drive Pal Driving School.'
    });
  },[]);

  const toggle=(id)=> setActivePolicy((prev)=> (prev===id ? null : id));

  return (
    <div className="min-h-screen pt-20 bg-brand-offwhite">
      <section className="bg-brand-navy py-12 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="font-display text-3xl md:text-5xl text-white mb-3">
            Compliance &amp; <span className="text-brand-gold">Policies</span>
          </h1>
          <p className="text-white/75 text-base md:text-lg">Information regarding our services, privacy, and responsibilities.</p>
        </div>
      </section>

      <section className="py-12 md:py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{opacity: 0,y: 16}}
            animate={{opacity: 1,y: 0}}
            className="mb-8 bg-brand-navy rounded-2xl p-5 md:p-6 shadow-lg border-l-4 border-brand-gold flex flex-col sm:flex-row sm:items-center gap-4"
          >
            <div className="bg-brand-gold/20 text-brand-gold p-3 rounded-xl shrink-0">
              <SafeIcon icon={FiAlertTriangle} className="text-2xl" />
            </div>
            <div>
              <h2 className="font-display text-xl text-white mb-1">Report Instructor Misconduct</h2>
              <p className="text-white/85">
                To report misconduct by a driving instructor, call Transport for NSW on{' '}
                <a href="tel:131500" className="text-brand-gold font-bold underline underline-offset-4 hover:text-white transition-colors">
                  131 500
                </a>.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{opacity: 0,y: 16}}
            animate={{opacity: 1,y: 0}}
            className="flex justify-center mb-10"
          >
            <div className="inline-flex items-center gap-3 bg-white px-5 py-3 rounded-full shadow-md border-l-4 border-brand-gold">
              <SafeIcon icon={FiClock} className="text-brand-navy text-lg shrink-0" />
              <span className="font-medium text-gray-700 text-sm md:text-base">
                Last Updated: <strong className="text-brand-navy font-display tracking-wide">15 April 2026</strong>
              </span>
            </div>
          </motion.div>

          <div className="space-y-5">
            {policies.map((policy,index)=> (
              <motion.div key={policy.id} initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} transition={{delay: index * 0.08}}>
                <PolicyCard policy={policy} isActive={activePolicy===policy.id} onToggle={()=> toggle(policy.id)} />
              </motion.div>
            ))}
          </div>

          <div className="mt-12 p-5 md:p-6 bg-brand-navy/5 rounded-xl border border-brand-navy/10 flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
            <p className="text-sm text-gray-700">
              <strong>Have a compliance question?</strong><br />
              Reach out and we'll respond promptly.
            </p>
            <a href="mailto:book.drivepal@gmail.com" className="bg-brand-navy text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-brand-gold hover:text-brand-navy transition-colors whitespace-nowrap">
              book.drivepal@gmail.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}